/**
 * Episode-aware date helpers for the Chief-complaints section.
 *
 * An episode in CC v2 is a contiguous run of status entries bounded by a
 * `vas === 0` marker (the equivalent of Dx's `ongoing === false`). Entries
 * with `event === "delete"` or `markedIncorrect: true` are excluded from
 * segmentation, matching the backend transformer's `countable` filter.
 *
 * Both helpers take a raw statusHistory array + a fallback value so they
 * work uniformly against a complaint record (`c.statusHistory`) and any
 * other context where only the audit array is in hand.
 */

const isCountable = (e) => !!e && e.event !== "delete" && !e.markedIncorrect;

/**
 * Start date of the MOST RECENT episode segment.
 *
 * For an ongoing complaint this is the current episode's start; for a
 * resolved complaint it is the start of the episode that just closed.
 * Algorithm: walk the countable audit backwards; skip a trailing resolve
 * (that closes the segment we want), find the previous resolve marker;
 * everything after it (or the whole chain if none) is the recent segment.
 * The FIRST entry of that segment carries the episode start `sinceWhen`.
 */
export const recentEpisodeSinceWhen = (statusHistory, fallback) => {
  const hist = Array.isArray(statusHistory) ? statusHistory : [];
  const countable = hist.filter(isCountable);
  if (countable.length === 0) return fallback;

  let i = countable.length - 1;
  if (countable[i].vas === 0) i -= 1;
  let boundary = -1;
  for (; i >= 0; i -= 1) {
    if (countable[i].vas === 0) {
      boundary = i;
      break;
    }
  }
  const start = countable[boundary + 1] || countable[0];
  return start.sinceWhen || fallback;
};

/**
 * Patient-reported date from the audit entry that CLOSED the most recent
 * episode — the last countable status row with `vas === 0`. Returns
 * `fallback` if no countable resolve exists.
 */
export const recentResolveSinceWhen = (statusHistory, fallback) => {
  const hist = Array.isArray(statusHistory) ? statusHistory : [];
  for (let i = hist.length - 1; i >= 0; i -= 1) {
    const e = hist[i];
    if (!isCountable(e)) continue;
    if (e.vas === 0) return e.sinceWhen || fallback;
  }
  return fallback;
};
